import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useApi, errMsg, fmtIST } from "@/lib/useApi";
import { Sparkles, Loader2, TrendingUp, TrendingDown, Minus, Target, Compass, BarChart3, RefreshCw } from "lucide-react";

const ST_CLS = { weak: "border-red-200 bg-red-50 text-red-700", developing: "border-amber-200 bg-amber-50 text-amber-700", strong: "border-emerald-200 bg-emerald-50 text-emerald-700", insufficient: "border-slate-200 text-slate-500", not_practised: "border-slate-200 text-slate-500" };

export default function InsightsPage() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");
  const [loadErr, setLoadErr] = useState("");

  const load = () => api.get("/insights").then((r) => { setD(r.data); setAi(r.data.ai_summary); }).catch((e) => setLoadErr(errMsg(e)));
  const plan = async () => {
    if (busy) return;
    setBusy(true); setAiErr("");
    try { await api.post("/insights/ai-summary"); }
    catch (e) { setAiErr(errMsg(e)); }
    await load();
    setBusy(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [api]);

  if (loadErr) return <p className="text-red-600 text-sm" data-testid="insights-load-error">{loadErr}</p>;
  if (!d) return <p className="text-slate-500">Loading…</p>;
  return (
    <div className="space-y-6 max-w-5xl" data-testid="insights-page">
      <div>
        <h1 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Performance insights</h1>
        <p className="text-slate-600 mt-2 text-sm">Measured from your saved, submitted attempts: {d.attempts} attempt(s) · {d.answered} answered · {d.skipped} skipped.</p>
      </div>
      <NextAction a={d.next_action} />
      {!d.sufficient && <Insufficient d={d} />}
      {d.sufficient && <WeakSection d={d} />}
      {d.sufficient && <StudyNext d={d} ai={ai} busy={busy} aiErr={aiErr} onGenerate={plan} />}
      <Improving d={d} />
      {d.subjects.length > 0 && <Subjects d={d} />}
    </div>
  );
}

const H = ({ icon: Icon, children, sub }) => (
  <div><h2 className="font-display font-semibold flex items-center gap-2 text-slate-900"><Icon className="h-4 w-4 text-blue-600" />{children}</h2>{sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}</div>
);
const Chg = ({ v }) => v == null ? <span className="text-xs text-slate-400">trend: not enough data</span>
  : <span className={`text-xs font-medium inline-flex items-center gap-0.5 ${v > 0 ? "text-emerald-700" : v < 0 ? "text-red-700" : "text-slate-500"}`}>{v > 0 ? <TrendingUp className="h-3 w-3" /> : v < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}{v > 0 ? "+" : ""}{v} pts</span>;

function NextAction({ a }) {
  return (
    <Card className="p-5 border-blue-200 bg-blue-50/60 flex flex-wrap items-center justify-between gap-4" data-testid="insights-next-action">
      <div className="min-w-0"><p className="text-xs uppercase tracking-wider text-blue-700 font-medium">Recommended next step · measured</p>
        <p className="font-display font-semibold text-slate-900 mt-1" data-testid="insights-next-action-title">{a.title}</p><p className="text-sm text-slate-600 mt-1">{a.text}</p></div>
      <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white shrink-0" data-testid="insights-next-action-btn"><Link to={a.link}>Go</Link></Button>
    </Card>
  );
}

function Insufficient({ d }) {
  return (
    <Card className="p-6 border-slate-200" data-testid="insights-insufficient">
      <p className="font-display font-semibold">Not enough data yet</p>
      <p className="text-sm text-slate-600 mt-2">Insights need at least {d.thresholds.attempts} submitted attempts and {d.thresholds.answered} answered questions
        (you have {d.attempts} and {d.answered}). A unit or subtopic is only called weak or strong after {d.thresholds.per_area}+ answered questions in it — we will not guess.</p>
    </Card>
  );
}

function AreaRow({ t, testid }) {
  return (
    <div className="py-3" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="min-w-0 truncate"><span className="font-medium text-slate-900">{t.subtopic || t.unit}</span>
          <span className="text-slate-400"> · {t.subtopic ? `${t.unit} · ` : ""}{t.subject}</span></span>
        <span className="font-mono shrink-0">{t.accuracy}%</span>
      </div>
      <Progress value={t.accuracy} className="h-1.5 mt-1.5" />
      <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500"><span>{t.correct} correct · {t.wrong} wrong · {t.skipped} skipped</span><Chg v={t.change} /></div>
    </div>
  );
}

function WeakSection({ d }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="p-6 border-slate-200 min-w-0" data-testid="insights-improvements">
        <H icon={Target} sub={`Below ${d.thresholds.weak_below}% over ${d.thresholds.per_area}+ answered, ranked by priority (accuracy gap × volume, boosted if declining).`}>What am I weak in?</H>
        <div className="divide-y divide-slate-100 mt-2">{d.improvements.length ? d.improvements.map((t, i) => <AreaRow key={i} t={t} testid={`insights-weak-${i}`} />)
          : <p className="text-sm text-slate-500 py-3">No unit or subtopic is below {d.thresholds.weak_below}% with enough evidence.</p>}</div>
        {d.insufficient_areas > 0 && <p className="text-xs text-slate-400 mt-2" data-testid="insights-insufficient-areas">{d.insufficient_areas} area(s) have fewer than {d.thresholds.per_area} answers and are not classified yet.</p>}
      </Card>
      <Card className="p-6 border-slate-200 min-w-0" data-testid="insights-strengths">
        <H icon={TrendingUp} sub={`${d.thresholds.strong_from}%+ over ${d.thresholds.per_area}+ answered.`}>Strengths</H>
        <div className="divide-y divide-slate-100 mt-2">{d.strengths.length ? d.strengths.map((t, i) => <AreaRow key={i} t={t} testid={`insights-strong-${i}`} />)
          : <p className="text-sm text-slate-500 py-3">No area has reached {d.thresholds.strong_from}% with enough evidence yet.</p>}</div>
      </Card>
    </div>
  );
}

function StudyNext({ d, ai, busy, aiErr, onGenerate }) {
  return (
    <Card className="p-6 border-slate-200 space-y-5" data-testid="insights-suggestions">
      <H icon={Compass} sub="Topics and figures below are measured from your attempts and the stored syllabus.">What should I study next?</H>
      {d.revise.length > 0 && <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="insights-revise">{d.revise.map((r, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-4 min-w-0">
          <p className="font-medium text-sm text-slate-900 truncate">{r.unit}</p><p className="text-xs text-slate-500 truncate">{r.subject} · {r.accuracy}% · {r.wrong} wrong</p>
          {r.subtopics.length ? <div className="flex flex-wrap gap-1.5 mt-3">{r.subtopics.map((s) => <Badge key={s.subtopic} variant="outline" className={`${ST_CLS[s.status]} text-[11px] font-normal`}>{s.subtopic}{s.accuracy != null ? ` · ${s.accuracy}%` : s.status === "not_practised" ? " · not practised" : ""}</Badge>)}</div>
            : <p className="text-xs text-slate-400 mt-3">{r.in_syllabus ? "No subtopics listed for this unit." : "Syllabus subtopics not defined for this unit yet."}</p>}
        </div>))}</div>}
      {d.suggestions.length ? <ul className="space-y-2 text-sm">{d.suggestions.map((s, i) => (
        <li key={i} className="flex flex-wrap items-start justify-between gap-3" data-testid={`insights-suggestion-${i}`}><span className="min-w-0 flex-1">{s.text}</span>
          {s.attempt_id ? <Link to={`/app/results/${s.attempt_id}`} className="text-blue-600 shrink-0">Review</Link> : <Link to="/app/practice" className="text-blue-600 shrink-0">Practise</Link>}</li>
      ))}</ul> : <p className="text-sm text-slate-500">No measured gaps — try a different subject or a full-length mock.</p>}
      <AiPlan ai={ai} busy={busy} err={aiErr} onGenerate={onGenerate} limits={d.ai_limits} />
    </Card>
  );
}

function AiPlan({ ai, busy, err, onGenerate, limits }) {
  const fresh = ai && !ai.stale;
  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-4" data-testid="insights-ai-summary">
      <p className="text-xs font-medium text-violet-700 flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />AI study plan — AI-generated interpretation of the measured figures above, not a score</p>
      {ai?.stale && <p className="text-xs text-amber-700 mt-2" data-testid="insights-ai-stale">This plan was based on earlier results. Update it to include your latest attempts.</p>}
      {ai?.plan && <div className="mt-2 space-y-2 text-sm text-slate-700">
        {ai.plan.summary && <p data-testid="insights-ai-text">{ai.plan.summary}</p>}
        {ai.plan.focus.map((f, i) => <p key={i}><span className="font-medium text-slate-900">{f.topic}: </span>{f.advice}</p>)}
        {ai.plan.next_step && <p className="font-medium text-violet-800">Next: {ai.plan.next_step}</p>}
      </div>}
      {ai && !ai.plan && ai.text && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap" data-testid="insights-ai-text">{ai.text}</p>}
      {busy && <p className="text-sm text-slate-600 mt-2 flex items-center gap-2" data-testid="insights-ai-loading"><Loader2 className="h-4 w-4 animate-spin" />Preparing your plan…</p>}
      {!busy && err && <p className="text-xs text-red-600 mt-2" data-testid="insights-ai-error">{err} Your measured statistics are unaffected.</p>}
      {!fresh && !busy && limits?.cooldown_until && <p className="text-xs text-slate-500 mt-2" data-testid="insights-ai-cooldown">You can request a new plan after {fmtIST(limits.cooldown_until)}.</p>}
      {!fresh && !busy && <Button size="sm" variant="outline" className="mt-3" onClick={onGenerate} disabled={!!limits?.cooldown_until || limits?.used_24h >= limits?.cap_24h} data-testid="insights-ai-btn">
        {ai ? <RefreshCw className="h-3.5 w-3.5 mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1 text-violet-600" />}{ai ? "Update AI study plan" : "Get AI study plan"}</Button>}
      {limits && <p className="text-[11px] text-slate-400 mt-2" data-testid="insights-ai-limits">{limits.used_24h} of {limits.cap_24h} AI plans used in the last 24 h · plans are reused until your results change</p>}
    </div>
  );
}

function Improving({ d }) {
  const p = d.progress;
  if (!p) return <Card className="p-6 border-slate-200" data-testid="insights-progress"><H icon={BarChart3}>Am I improving?</H><p className="text-sm text-slate-500 mt-3">Progress comparison appears after your second submitted attempt.</p></Card>;
  const V = { improving: "text-emerald-700", declining: "text-red-700", steady: "text-slate-700" };
  return (
    <Card className="p-6 border-slate-200 space-y-4" data-testid="insights-progress">
      <H icon={BarChart3} sub={`Earlier ${p.early_attempts} vs recent ${p.recent_attempts} attempt(s) — measured.`}>Am I improving?</H>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Mini label="Verdict" value={<span className={`capitalize ${V[p.verdict]}`} data-testid="insights-verdict">{p.verdict}</span>} />
        <Mini label="Accuracy earlier → recent" value={`${p.early_accuracy}% → ${p.recent_accuracy}%`} />
        <Mini label="Change" value={<Chg v={p.accuracy_change} />} />
        <Mini label="Latest vs previous" value={<Chg v={p.latest_vs_previous} />} />
      </div>
      {(p.improved_areas.length > 0 || p.declined_areas.length > 0) && <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div><p className="text-xs font-medium text-emerald-700">Improved units</p>{p.improved_areas.map((t, i) => <p key={i} className="mt-1 truncate">{t.unit} <Chg v={t.change} /></p>)}{!p.improved_areas.length && <p className="text-xs text-slate-400 mt-1">—</p>}</div>
        <div><p className="text-xs font-medium text-red-700">Declined units</p>{p.declined_areas.map((t, i) => <p key={i} className="mt-1 truncate">{t.unit} <Chg v={t.change} /></p>)}{!p.declined_areas.length && <p className="text-xs text-slate-400 mt-1">—</p>}</div>
      </div>}
      <div className="h-56" data-testid="insights-trend">
        <ResponsiveContainer><LineChart data={d.trend.map((t, i) => ({ n: i + 1, percent: t.percent, accuracy: t.accuracy }))}>
          <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" /><XAxis dataKey="n" tick={{ fontSize: 12 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 12 }} /><Tooltip />
          <Line type="monotone" dataKey="percent" name="Score %" stroke="#2563EB" strokeWidth={2} /><Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke="#0D9488" strokeWidth={2} />
        </LineChart></ResponsiveContainer>
      </div>
    </Card>
  );
}
const Mini = ({ label, value }) => <div className="rounded-lg border border-slate-200 p-3 min-w-0"><p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p><div className="mt-1 font-medium text-slate-900">{value}</div></div>;

function Subjects({ d }) {
  return (
    <Card className="p-6 border-slate-200" data-testid="insights-subjects">
      <h2 className="font-display font-semibold">Accuracy by subject</h2>
      <div className="mt-4 space-y-3">{d.subjects.map((s) => (
        <div key={s.key}><div className="flex justify-between gap-3 text-sm"><span className="truncate">{s.subject}</span><span className="font-mono shrink-0">{s.accuracy}% · {s.attempted} q</span></div>
          <Progress value={s.accuracy} className="h-2 mt-1" /><div className="flex gap-3 mt-1 text-xs text-slate-500"><span>{s.correct} correct · {s.wrong} wrong · {s.skipped} skipped</span><Chg v={s.change} /></div></div>
      ))}</div>
    </Card>
  );
}
